/**
 * /w/:slug/app/programs/:program/operate/mock-audit — Mock audit (V98-5-7).
 * An INTERNAL working document, never audit evidence.
 *
 *   Known deviations to disclose  already failed in the period (not
 *                                 performed by its date, done late, a deadline
 *                                 missed). Never listed as fixable: the action
 *                                 is a management response (Start / Continue /
 *                                 Submitted · with <approver> / Revise).
 *   Not yet failed · due soon     fixable before it is sampled.
 *   Can't be tested               no evidence / no control.
 *   Partial population            connectors that missed their cadence.
 *
 * Reads: GET /programs/:program/mock-audit (any member). Writes:
 * POST /management-responses (the deviation's owner, the lead, owner/admin).
 */
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, PageHeader, Spinner } from '@aigovops/design-system';
import type { MockAuditRow } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import { getMockAudit, startManagementResponse } from '../lib/cert-ops-api';
import { fmtDate } from '../lib/cert-ops-model';
import { PROGRAM_LABELS, isProgramKey } from '../lib/nav-config';
import { firstName, nameOf, type Names } from '../lib/today-model';
import { Chip, errorText } from '../components/today/shared';
import { useNames } from './Today';
import './today.css';
import './programs.css';
import './cert-ops.css';

export function MockAudit() {
  const { program = '' } = useParams<{ program: string }>();
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const me = useAuth().user?.id ?? '';
  const isAdmin = role === 'owner' || role === 'admin';
  const names = useNames(slug);
  const q = useQuery({ queryKey: ['mock-audit', slug, program], queryFn: () => getMockAudit(slug, program), enabled: isProgramKey(program), retry: false });
  if (!isProgramKey(program)) return <Navigate to={`${prefix}/programs`} replace />;
  const label = PROGRAM_LABELS[program] ?? program;
  const a = q.data?.audit;
  const known = a?.rows.filter((r) => r.result === 'known_deviation') ?? [];
  const fix = a?.rows.filter((r) => r.result === 'fix_before_sampled') ?? [];
  const cant = a?.rows.filter((r) => r.result === 'cant_test') ?? [];
  return (
    <div className="gp-item">
      <nav className="gp-crumbs" aria-label="Breadcrumb">
        <Link to={`${prefix}/programs`}>Programs</Link><span aria-hidden="true">/</span>
        <Link to={`${prefix}/programs/${program}/operate`}>{`${label} · Operate`}</Link><span aria-hidden="true">/</span>
        <span aria-current="page">Mock audit</span>
      </nav>
      <PageHeader variant="app" title={a ? `Mock audit · ${a.period.label}` : 'Mock audit'} headingId="mock-h1"
        subtitle={a ? `${fmtDate(a.period.start)} – ${fmtDate(a.period.as_of)} · run ${new Date(a.run.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : undefined} />
      <p><span className="gp-internal">Internal working document · not audit evidence</span></p>
      {q.isLoading ? <Spinner label="Running the mock audit" />
        : q.error || !a ? <Alert variant="error"><p>The mock audit could not run.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
          : (
            <>
              <p className="gp-counts" data-part="mock-counts">
                <span><b className={a.counts.would_fail ? 'gp-bad' : undefined}>{a.counts.would_fail}</b> would fail sampling</span>
                <span><b>{a.counts.known_deviation}</b> known deviations</span>
                <span><b>{a.counts.fix_before_sampled}</b> fixable before sampling</span>
                <span><b>{a.counts.cant_test}</b> can’t be tested</span>
                {/* T5-B5: sampled items, not criteria — named so it never reads against "N of M met". */}
                <span><b>{a.counts.pass}</b> sampled items pass</span>
              </p>
              {a.caveats.length ? (
                <div className="gp-state gp-state--warn" data-part="mock-caveats">
                  <strong>Partial population</strong>
                  <ul className="gp-plain-list">{a.caveats.map((x) => <li key={x.connector_id}>{x.text} <Link to={`${prefix}/settings/integrations`}>Reconnect</Link></li>)}</ul>
                </div>
              ) : null}
              <Section id="known" title={`Known deviations to disclose · ${known.length}`} empty="Nothing has failed in the period so far.">
                {known.map((r) => <KnownRow key={r.key} r={r} slug={slug} prefix={prefix} program={program} names={names} canStart={isAdmin || (!!me && r.owner_user_id === me)} />)}
              </Section>
              <Section id="soon" title={`Not yet failed · due soon · ${fix.length}`} empty="Nothing is close to failing.">
                {fix.map((r) => <PlainRow key={r.key} r={r} prefix={prefix} program={program} names={names} />)}
              </Section>
              {cant.length ? (
                <Section id="cant" title={`Can’t be tested · ${cant.length}`} empty="">
                  {cant.map((r) => <PlainRow key={r.key} r={r} prefix={prefix} program={program} names={names} />)}
                </Section>
              ) : null}
            </>
          )}
    </div>
  );
}

function Section({ id, title, empty, children }: { id: string; title: string; empty: string; children: React.ReactNode }) {
  const items = Array.isArray(children) ? children : [children];
  return (
    <section className="gp-sec" aria-labelledby={`mock-${id}`}>
      <h2 id={`mock-${id}`} className="gp-sec-h">{title}</h2>
      {items.filter(Boolean).length ? <ul className="gp-owed-list" aria-labelledby={`mock-${id}`}>{children}</ul> : <p className="gp-dim">{empty}</p>}
    </section>
  );
}

const hrefOf = (r: MockAuditRow, prefix: string, program: string) => !r.ref ? null
  : r.ref.kind === 'requirement' ? `${prefix}/programs/${program}/prepare`
    : `${prefix}/items/${r.ref.kind}/${encodeURIComponent(r.ref.id)}?from=program:${program}`;

function PlainRow({ r, prefix, program, names }: { r: MockAuditRow; prefix: string; program: string; names: Names }) {
  const to = hrefOf(r, prefix, program);
  const here = useLocation();
  // F21: the upload names what it is for and returns here on save.
  const addHref = `${prefix}/evidence?new=upload&control=${encodeURIComponent(r.ref?.id ?? r.key)}&for=${encodeURIComponent(r.title)}&return=${encodeURIComponent(here.pathname + here.search)}`;
  return (
    <li className="gp-ow" data-mock={r.result}>
      <span className="gp-ow-main">
        {to ? <Link className="gp-ow-t" to={to}>{r.title}</Link> : <span className="gp-ow-t">{r.title}</span>}
        <span className="gp-ow-meta">
          <Chip text={r.result === 'fix_before_sampled' ? 'Would fail' : 'Can’t test'} tone={r.result === 'fix_before_sampled' ? 'amber' : 'neutral'} />
          <span>{r.why}</span>
          <span>{r.owner_user_id ? nameOf(names, r.owner_user_id) : 'Unassigned'}</span>
        </span>
      </span>
      {/* V102-8-3 (MockAudit dead end): a row that can't be tested says what makes it testable — add evidence, or map a control. */}
      {r.result === 'cant_test'
        ? <span className="gp-ow-act" data-part="cant-test-act">{r.why === 'No control mapped'
            ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/common-controls`} aria-label={`Map a control · ${r.title}`}>Map a control</Link>
            : <Link className="gp-linkbtn gp-linkbtn--compact" to={addHref} aria-label={`Add evidence · ${r.title}`}>Add evidence</Link>}</span>
        : to ? <span className="gp-ow-act"><Link className="gp-linkbtn gp-linkbtn--compact" to={to} aria-label={`Open · ${r.title}`}>Open</Link></span> : null}
    </li>
  );
}

function KnownRow({ r, slug, prefix, program, names, canStart }: { r: MockAuditRow; slug: string; prefix: string; program: string; names: Names; canStart: boolean }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const resp = r.response!;
  const start = async () => {
    setBusy(true); setErr(null);
    try { const x = await startManagementResponse(slug, program, r.key); navigate(`${prefix}/responses/${encodeURIComponent(x.response.work_item.work_item_id)}?from=mock:${program}`); }
    catch (e) { setErr(errorText(e, 'The response could not be started.')); }
    finally { setBusy(false); }
  };
  const open = resp.work_item_id ? `${prefix}/responses/${encodeURIComponent(resp.work_item_id)}?from=mock:${program}` : null;
  // T5-H5: red = needs action now, neutral = in progress / waiting.
  const state = resp.state === 'not_started' ? { text: 'Response not started', tone: 'red' as const }
    : resp.state === 'drafting' ? { text: 'Response drafting', tone: 'neutral' as const }
      : resp.state === 'submitted' ? { text: `Response with ${resp.approver_user_id ? firstName(nameOf(names, resp.approver_user_id)) : 'the approver'}`, tone: 'neutral' as const }
        : resp.state === 'returned' ? { text: 'Sent back', tone: 'red' as const } : { text: 'Response signed', tone: 'ok' as const };
  return (
    <li className="gp-ow" data-mock="known_deviation">
      <span className="gp-ow-main">
        <span className="gp-ow-line"><span className="gp-ow-t">{r.title}</span>{r.sampled ? <span className="gp-dim">{`sampled ${r.sampled}`}</span> : null}</span>
        <span className="gp-ow-meta">
          <Chip text="Known deviation" tone="red" />
          <span>{r.why}</span>
          <span>{r.owner_user_id ? nameOf(names, r.owner_user_id) : 'Unassigned'}</span>
        </span>
        <span className="gp-ow-meta"><Chip text={state.text} tone={state.tone} />{resp.reason ? <span>{resp.reason}</span> : null}</span>
        {err ? <Alert variant="error">{err}</Alert> : null}
      </span>
      <span className="gp-ow-act">
        {resp.state === 'not_started' && !open
          ? (canStart
            ? <Button compact aria-disabled={busy || undefined} onClick={() => void start()} aria-label={`Write response · ${r.title}`}>{busy ? 'Starting…' : 'Write response'}</Button>
            : <span className="gp-dim">{`${r.owner_user_id ? firstName(nameOf(names, r.owner_user_id)) : 'Its owner'} writes it`}</span>)
          : open ? <Link className="gp-linkbtn gp-linkbtn--compact" to={open} aria-label={`${resp.state === 'returned' ? 'Revise' : resp.state === 'drafting' || resp.state === 'not_started' ? 'Write' : 'Open'} response · ${r.title}`}>
            {resp.state === 'returned' ? 'Revise response' : resp.state === 'drafting' || resp.state === 'not_started' ? 'Write response' : 'Open response'}</Link> : null}
      </span>
    </li>
  );
}
